import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  Sparkles,
  Compass,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
} from 'lucide-react';
import Layout from '../../components/layout/layout';
import Section from '../../components/ui/section';
import SectionBackground from '../../components/sections/section-background';
import MappingRHTool from '../../components/mapping-rh/MappingRHTool';
import { WebPageSchema, BreadcrumbSchema } from '../../lib/utils/schema-utils';

// — Typography aligned on home / campagnes / recrutement / candidat ——
const gradientText =
  'text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]';

const eyebrowClass =
  'inline-flex items-center rounded-full bg-white px-5 py-2 text-sm font-semibold text-neutral-500 uppercase tracking-widest shadow-sm ring-1 ring-neutral-200 mb-6';

const h2Class =
  'text-4xl md:text-5xl font-bold tracking-tight text-[#30475e] mb-5 leading-[1.08]';

const h3Class =
  'text-xl font-semibold text-[#30475e] mb-3 leading-snug';

const bodyClass =
  'text-base text-neutral-600 leading-relaxed';

const sectionSubtitleClass = 'text-lg md:text-xl text-neutral-600 leading-relaxed';

// — Section 2 — 3 étapes du test ————————————————————————————————————
const steps = [
  {
    icon: Compass,
    title: 'Décrivez votre équipe en 5 curseurs',
    description:
      "Autonomie, feedback, ambiance, structuration, prise d'initiative : 5 curseurs pour cartographier le style réel de votre équipe.",
  },
  {
    icon: Sparkles,
    title: 'Collez le profil que vous voulez recruter',
    description:
      "Description libre du profil cible, ou copier-coller direct de votre fiche de poste. L'IA fait la lecture sémantique.",
  },
  {
    icon: Brain,
    title: 'Recevez votre rapport en 30 secondes',
    description:
      "Score de compatibilité 0-100, zones de friction probables, recommandations concrètes pour le manager. Tout en moins de 3 minutes.",
  },
];

// — Section 4 — 3 cartes valeur ————————————————————————————————————
const valueCards = [
  {
    icon: AlertTriangle,
    title: 'Identifier les frictions avant de signer',
    description:
      "Mieux vaut découvrir les points de tension à 30 secondes qu'à 3 mois. Le test révèle les écarts qui passent inaperçus en entretien classique.",
  },
  {
    icon: CheckCircle2,
    title: 'Outiller le manager dès le jour 1',
    description:
      "Les recommandations livrées sont actionnables : ajustement du style de management, adaptation des rituels, points de vigilance précis.",
  },
  {
    icon: RefreshCw,
    title: 'Tester autant de profils que vous voulez',
    description:
      "Refaire le test avec un autre profil candidat ne coûte rien. Comparez plusieurs candidats sur les mêmes critères en quelques minutes.",
  },
];

export default function MappingRHPage() {
  const navigate = useNavigate();

  return (
    <Layout
      title="Mapping RH · Test gratuit de compatibilité candidat ↔ équipe (GPT-4) | recruteurs.io"
      description="Outil gratuit propulsé par GPT-4 : évaluez en 3 minutes la compatibilité entre un candidat et le style réel de votre équipe. Score 0-100, frictions probables, recommandations actionnables pour le manager. Conçu par recruteurs.io, utilisé en interne depuis 2017."
      keywords="mapping rh, compatibilité candidat equipe, test recrutement gratuit, outil rh ia, gpt-4 rh, friction recrutement, recruteurs.io"
    >
      <WebPageSchema
        page={{
          title: 'Mapping RH — Test de compatibilité candidat ↔ équipe | recruteurs.io',
          description:
            "Outil gratuit de Mapping RH propulsé par GPT-4 : évaluez en 3 minutes la compatibilité entre un candidat et le style réel de votre équipe. Score, frictions, recommandations.",
          url: 'https://recruteurs.io/ressources/mapping-rh',
          lastReviewed: '2026-05-10',
        }}
      />

      <BreadcrumbSchema
        items={[
          { name: 'Accueil', url: 'https://recruteurs.io' },
          { name: 'Ressources', url: 'https://recruteurs.io/ressources/mapping-rh' },
          { name: 'Mapping RH', url: 'https://recruteurs.io/ressources/mapping-rh' },
        ]}
      />

      {/* HERO — layout centré, gradient pastel pur (sans image en fond pour lisibilité) */}
      <SectionBackground
        page="mapping-rh"
        section="mapping-hero"
        className="relative overflow-hidden bg-gradient-to-br from-[#fff1f2] via-[#fef9c3] to-[#e0f2ff]"
      >
        {/* Décoration subtile : blobs gradient brand très diffus (pas d'image UI conflictuelle) */}
        <div
          aria-hidden
          className="absolute inset-0 z-0 pointer-events-none select-none"
        >
          <div className="absolute -top-32 -left-32 w-96 h-96 bg-[#d03484]/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-[#d47254]/10 rounded-full blur-3xl" />
        </div>

        <section className="relative z-10 min-h-[88vh] flex items-center py-10 md:py-12 lg:py-14">
          <div className="w-full px-[5vw] xl:px-[100px] max-w-[1440px] mx-auto">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-xs md:text-sm font-semibold text-neutral-500 mb-3 leading-snug uppercase tracking-widest">
                Outil gratuit · IA GPT-4 · 3 minutes
              </p>

              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-[3.5rem] font-title font-extrabold leading-[1.1] pb-1 mb-0">
                <span className="text-[#30475e]">Mapping RH&nbsp;: votre candidat colle </span>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#d03484] to-[#d47254]">
                  vraiment à votre équipe&nbsp;?
                </span>
              </h1>

              <p className="mt-5 mb-8 text-neutral-800 text-base md:text-lg leading-relaxed max-w-2xl mx-auto">
                En 5 curseurs et une description de poste, obtenez un score de
                compatibilité candidat ↔ équipe, les zones de friction probables
                et des recommandations actionnables pour le manager. Conçu et
                utilisé en interne chez recruteurs.io depuis 2017, ouvert à
                tous depuis 2025.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={() => {
                    const el = document.getElementById('mapping-tool');
                    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
                >
                  Lancer le test
                </button>
                <button
                  onClick={() => navigate('/decouvrir')}
                  className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
                >
                  Voir le Parcours Découverte
                </button>
              </div>
            </div>
          </div>
        </section>
      </SectionBackground>

      {/* SECTION 2 — Comment ça marche (3 étapes) */}
      <SectionBackground page="mapping-rh" section="mapping-how" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Comment ça marche</span>

            <h2 className={h2Class}>
              Trois étapes,{' '}
              <span className={gradientText}>moins de trois minutes.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Le test analyse l’écart entre le style réel de votre équipe et le
              profil candidat que vous décrivez. Pas de jargon RH, pas de longue
              psychométrie — un outil opérationnel pour managers et recruteurs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={step.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.6, delay: index * 0.08 }}
                  className="rounded-[1.75rem] bg-white p-8 shadow-sm ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-xl transition-all duration-300"
                >
                  <div className="mb-8 flex items-center justify-between">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white font-bold shadow-lg shadow-[#d03484]/20">
                      0{index + 1}
                    </div>
                    <Icon className="w-6 h-6 text-neutral-300" />
                  </div>

                  <h3 className={h3Class}>{step.title}</h3>
                  <p className={bodyClass}>{step.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 3 — L'outil (le composant principal) */}
      <SectionBackground page="mapping-rh" section="mapping-tool" className="bg-[#fbfaf8]">
        <Section>
          <div id="mapping-tool" className="max-w-4xl mx-auto text-center mb-12 scroll-mt-24">
            <span className={eyebrowClass}>L’outil</span>

            <h2 className={h2Class}>
              À vous de jouer.{' '}
              <span className={gradientText}>Lancez votre test.</span>
            </h2>

            <p className={sectionSubtitleClass}>
              Vous obtiendrez un score de friction managériale et des
              recommandations d’intégration immédiates.
            </p>
          </div>

          <MappingRHTool />
        </Section>
      </SectionBackground>

      {/* SECTION 4 — Pourquoi c'est utile */}
      <SectionBackground page="mapping-rh" section="mapping-value" className="bg-white">
        <Section>
          <div className="max-w-4xl mx-auto text-center mb-14">
            <span className={eyebrowClass}>Pourquoi c’est utile</span>

            <h2 className={h2Class}>
              Trois bénéfices concrets,{' '}
              <span className={gradientText}>tout de suite.</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {valueCards.map((card, index) => {
              const Icon = card.icon;
              return (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-80px' }}
                  transition={{ duration: 0.5, delay: index * 0.06 }}
                  className="rounded-[1.75rem] bg-[#fbfaf8] p-8 ring-1 ring-neutral-200 hover:-translate-y-1 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] flex items-center justify-center text-white mb-5 shadow-lg shadow-[#d03484]/20">
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className={h3Class}>{card.title}</h3>
                  <p className={bodyClass}>{card.description}</p>
                </motion.div>
              );
            })}
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 5 — Notre conviction IA + recrutement */}
      <SectionBackground page="mapping-rh" section="mapping-philosophy" className="bg-[#fbfaf8]">
        <Section>
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <span className={eyebrowClass}>Notre conviction</span>

              <h2 className={h2Class}>
                L’IA dans le recrutement, ce n’est pas un gadget.{' '}
                <span className={gradientText}>C’est notre quotidien depuis 2017.</span>
              </h2>
            </div>

            <div className="rounded-[2rem] bg-white p-8 md:p-10 ring-1 ring-neutral-200 shadow-sm space-y-5">
              <p className={bodyClass}>
                Chez <strong>recruteurs.io</strong>, on ne suit pas les tendances —
                on les précède. Depuis 2017, on explore, on teste, on confronte
                l’intelligence artificielle à la réalité du terrain RH. Notre
                conviction est simple : <strong>l’IA ne remplace pas le recruteur,
                elle l’amplifie</strong>.
              </p>
              <p className={bodyClass}>
                C’est pourquoi on ne garde rien pour nous. Les outils qu’on utilise
                en interne, on les met à disposition de tous — gratuitement quand
                c’est possible. Si on veut vraiment faire évoluer les pratiques RH,
                on doit ouvrir les portes, pas les verrouiller.
              </p>
              <p className={bodyClass}>
                Le <strong>Mapping RH</strong> en est un parfait exemple. Un test
                simple, intuitif, qui vous permet d’évaluer l’adéquation
                culturelle entre un candidat et votre équipe. Pas pour juger,
                mais pour <strong>comprendre, fluidifier, sécuriser</strong>.
              </p>
              <p className={bodyClass}>
                Vous voyez la partie émergée de l’iceberg. Derrière, il y a une
                vraie mission : rendre les recrutements plus humains, plus
                transparents, et plus performants.
              </p>
            </div>
          </div>
        </Section>
      </SectionBackground>

      {/* SECTION 6 — CTA final */}
      <SectionBackground page="mapping-rh" section="mapping-cta" className="bg-white">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className={h2Class}>
              Vous voulez aller plus loin que le test ?{' '}
              <span className={gradientText}>Parlons de votre prochain recrutement.</span>
            </h2>
            <p className={`${sectionSubtitleClass} mb-10`}>
              Le Mapping RH cartographie un écart. Notre méthode complète — chasse
              de tête, IA appliquée au sourcing, Plateforme Parcours Découverte —
              le résorbe avant même la signature.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <button
                onClick={() => navigate('/reservation')}
                className="inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-[#d03484] to-[#d47254] px-6 py-2.5 text-sm font-bold whitespace-nowrap text-white shadow-lg shadow-[#d03484]/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                Réserver un échange (15 min)
              </button>
              <button
                onClick={() => navigate('/decouvrir')}
                className="inline-flex items-center gap-2 rounded-2xl bg-white text-[#30475e] px-6 py-2.5 text-sm font-bold whitespace-nowrap ring-1 ring-neutral-200 hover:ring-neutral-300 hover:-translate-y-0.5 transition-all"
              >
                Voir le Parcours Découverte
              </button>
            </div>
          </div>
        </Section>
      </SectionBackground>
    </Layout>
  );
}
